<!-- eslint-disable no-unreachable -->
<template>
  <!-- Skeleton placeholder while deal loads -->
  <SkeletonLoading v-if="isLoading" />

  <div
    v-else-if="deal && bundleAvailable"
    class="py-8"
    :class="[applyClassesDependingOnBundleSize('md:bg-putty xl:py-20', 'md:bg-putty lg:py-20', 'md:bg-putty md:py-20')]"
  >
    <div
      class="flex flex-wrap items-center"
      :class="[
        applyClassesDependingOnBundleSize(
          'xl:flex-nowrap xl:px-8 max-w-screen-2xl mx-auto',
          'lg:flex-nowrap sm:px-4 lg:px-8 max-w-screen-2xl mx-auto',
          'md:flex-nowrap container'
        ),
      ]"
    >
      <div
        class="flex flex-col justify-center w-full text-center relative shrink-0"
        :class="[
          applyClassesDependingOnBundleSize('xl:text-left xl:pr-4', 'lg:text-left lg:pr-4', 'md:text-left md:pr-4'),
          applyClassesDependingOnBundleSize(
            'xl:max-w-xs',
            'lg:max-w-xs lg:mr-10',
            'md:max-w-xs lg:max-w-sm 2xl:max-w-lg'
          ),
        ]"
      >
        <div class="w-full text-br-green fluid-sm leading-tight">
          {{ overline }}
        </div>
        <div class="fluid-3xl leading-tightest font-semibold my-4 w-full">{{ heading }}</div>
        <div>{{ bundleNames }}</div>
        <CrossSellAction
          :price="bundlePrice"
          :compare-price="
            showComparePrices && bundleCompareAtPrice.amount !== bundlePrice.amount ? bundleCompareAtPrice : null
          "
          :is-available="bundleAvailable"
          :is-loading="isSubmitting"
          :show-savings="showSavingsLabelXSell"
          class="hidden w-full mt-4"
          :class="[applyClassesDependingOnBundleSize('xl:block', 'lg:block', 'md:block')]"
          @addToCart="addToCart"
        />
      </div>
      <div
        class="w-full mt-6 mx-auto"
        :class="[
          layoutClasses,
          applyClassesDependingOnBundleSize('xl:mt-0 xl:w-auto', 'lg:mt-0 lg:w-auto', 'md:mt-0 md:w-auto'),
        ]"
      >
        <ProductCardLight
          v-for="(item, index) in productsList"
          :key="index"
          v-bind="{
            title: item.title,
            description: item.productCardUsp,
            handle: item.handle,
            image: item.selected.image,
            price: item.displayPrice,
            compareAtPrice: item.displayComparePrice,
            unitPrice: item.selected.unitPrice,
            unitPriceMeasurement: item.selected.unitPriceMeasurement,
            variants: item.variants,
            selectedId: item.selected.id,
            showComparePrices,
            showSavings: false,
          }"
          v-track:view.item="gtmData(item, index)"
          v-track:click.item="gtmData(item, index)"
          class="last:after:hidden after:content-['+'] after:absolute after:top-1/2 after:left-full after:font-bold after:text-center after:-mt-4"
          :class="[
            applyClassesDependingOnBundleSize(
              'after:w-10 lg:after:w-8 after:fluid-xl leading-tight font-medium',
              'w-4 sm:after:w-10 lg:after:w-16 xl:after:w-24 after:fluid-sm sm:after:fluid-3xl leading-tightest',
              'w-4 sm:after:w-10 lg:after:w-24 after:fluid-lg sm:after:fluid-3xl leading-tightest'
            ),
          ]"
          @update:selected="updateSelectedItem($event, index)"
        />
      </div>

      <!-- This is the mobile Add To Cart button -->
      <CrossSellAction
        :price="bundlePrice"
        :compare-price="
          showComparePrices && bundleCompareAtPrice.amount !== bundlePrice.amount ? bundleCompareAtPrice : null
        "
        :is-available="bundleAvailable"
        :is-loading="isSubmitting"
        :show-savings="showSavingsLabelXSell"
        class="text-center block w-full mt-4"
        :class="[applyClassesDependingOnBundleSize('xl:hidden', 'lg:hidden', 'md:hidden')]"
        @addToCart="addToCart"
      />
    </div>

    <PreAssessmentModal
      v-if="hasPreAssessment && showPreAssessmentModal"
      :showing="showPreAssessmentModal"
      :info="agreementCategory[0]"
      @buy="addDealToCart"
      @close="showPreAssessmentModal = false"
    />
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useCartStore } from '@/stores/cart';
import { useAppStore } from '@/stores/app';
import { useGlobalBannerStore } from '@/stores/global-banner';
import CrossSellAction from './CrossSellAction.vue';
import SkeletonLoading from './SkeletonLoading.vue';
import ProductCardLight from '~/components/shop/products/ProductCardLight';
import PreAssessmentModal from '~/components/common/blocks/PreAssessmentModal';
import { Bus } from '@/utils/bus';
import { getAddToCartParamsFromUrl } from '@/utils/product';
import { usePickystoryDeal } from '~/composables/usePickystoryDeal';

defineNuxtComponent({
  name: 'CrossSell',
});

// Props definition with defineProps
const props = defineProps({
  heading: {
    type: String,
    default: null,
  },
  overline: {
    type: String,
    default: null,
  },
  product: {
    type: Object,
    default: null,
  },
  useProductOverwrite: {
    type: Boolean,
    default: false,
  },
  triggerProductOverwrite: {
    type: String,
    default: null,
  },
  pickystoryDealType: {
    type: String,
    default: null,
  },
  agreementCategory: {
    type: Array,
    default: () => null,
  },
  showComparePrices: {
    type: Boolean,
    default: true,
  },
});

const { $pickystory } = useNuxtApp();
const { $dlPush } = useNuxtApp();
// Store and route access
const cartStore = useCartStore();
const appStore = useAppStore();
const globalBannerStore = useGlobalBannerStore();
const route = useRoute();

const showSavingsLabelXSell = computed(() => globalBannerStore.showSavingsLabelXSell);

// Data properties as refs
const deal = ref(null);
const isSubmitting = ref(false);
const showPreAssessmentModal = ref(false);
const isLoading = ref(true); // Start as true to show skeleton immediately

// Use composable for reactive pricing and variant management
const {
  productsList,
  bundlePrice,
  bundleCompareAtPrice,
  updateVariant: updateVariantInComposable,
  getVariantsForCart,
  syncVariantById,
} = usePickystoryDeal(deal);

// Computed properties
const hasPreAssessment = computed(() => props.agreementCategory?.length > 0);

const bundleSize = computed(() => productsList.value?.length ?? 0);

const layoutClasses = computed(() => {
  return {
    'grid grid-cols-2 gap-x-10 lg:gap-x-24': bundleSize.value === 1,
    'grid grid-cols-2 gap-x-10 lg:gap-x-24': bundleSize.value === 2,
    'grid grid-cols-3 gap-x-4 sm:gap-x-10 lg:gap-x-16 xl:gap-x-24': bundleSize.value === 3,
    'grid grid-cols-4 gap-x-10 lg:gap-x-8': bundleSize.value === 4,
  };
});

const bundleAvailable = computed(() => {
  if (!productsList.value?.length) {
    return false;
  }

  try {
    return !productsList.value.some((product) => {
      return !product.variants.some((variant) => {
        return variant.available;
      });
    });
  } catch (e) {
    return false;
  }
});

const bundleNames = computed(() => productsList.value?.map((product) => product.title).join(' + '));

// Methods as regular functions
const initDeal = async () => {
  const triggerHandle =
    props.useProductOverwrite && props.triggerProductOverwrite && props.triggerProductOverwrite.length
      ? props.triggerProductOverwrite
      : props.product.handle;

  const dealType = props.pickystoryDealType === 'bnd' ? $pickystory.types.BND_TYPE : $pickystory.types.FBT_TYPE;
  const locationName = props.pickystoryDealType === 'bnd' ? null : 'xsell-pdp';

  try {
    deal.value = await $pickystory.findDeal(locationName, triggerHandle, dealType);
  } catch (error) {
    console.warn(error);
  } finally {
    isLoading.value = false;
  }
};

const updateSelectedItem = (selected, index) => {
  const product = productsList.value[index];
  updateVariantInComposable(product.id, parseInt(selected, 10));
};

const applyClassesDependingOnBundleSize = (largeClasses, mediumClasses, smallClasses) => {
  return {
    [largeClasses]: bundleSize.value === 4,
    [mediumClasses]: bundleSize.value === 3,
    [smallClasses]: bundleSize.value === 2 || bundleSize.value === 1,
  };
};

const addToCart = (quantity = 1) => {
  if (hasPreAssessment.value) {
    showPreAssessmentModal.value = true;
  } else {
    addDealToCart(quantity);
  }
};

const addDealToCart = (quantity = 1) => {
  if (props.pickystoryDealType === 'bnd') {
    addBNDtoCart(quantity);
  } else {
    addFBTtoCart(quantity);
  }
};

const addFBTtoCart = async (quantity = 1) => {
  try {
    await cartStore.initCart();
    isSubmitting.value = true;

    const { products, addonProducts } = getVariantsForCart();

    const rawDeal = toRaw(deal.value.pickystory);
    await rawDeal.addVariantsToCart(products, quantity, addonProducts);

    // GTM tracking with exact discounted prices
    const variantsWithProducts = productsList.value.map((product) => ({
      variant: {
        ...product.selected,
        price: {
          ...product.selected.price,
          amount: product.exactDiscountedPrice,
        },
      },
      product: {
        title: product.title,
        productType: product.productType || '',
        handle: product.handle,
        imageUrl: product.selected.image,
      },
    }));
    $dlPush.eECaddToCart(variantsWithProducts);

    await cartStore.pickystoryRefreshCart();
    await cartStore.refreshCheckout();

    cartStore.open();
  } catch (error) {
    console.warn(error);
  } finally {
    isSubmitting.value = false;
  }
};

const addBNDtoCart = async (quantity = 1) => {
  try {
    await cartStore.initCart();
    isSubmitting.value = true;

    const { products } = getVariantsForCart();

    // BND deals: remove existing items first
    await cartStore.removeLineItems(products.map((product) => product.id));

    const rawDeal = toRaw(deal.value.pickystory);
    await rawDeal.addVariantsToCart(products, quantity);

    await cartStore.pickystoryRefreshCart();
    await cartStore.refreshCheckout();

    cartStore.open();
  } catch (error) {
    console.warn(error);
  } finally {
    isSubmitting.value = false;
  }
};

const gtmData = (payload, index) => {
  try {
    const variant = payload.selected;
    const itemSKU = variant.sku;

    return {
      item_list_id: [route?.name, props.heading.toLowerCase().replace(/ /g, '-')].join(' / '),
      item_list_name: [route?.name, props.heading].join(' / '),
      index,
      item_id: itemSKU,
      price: payload.exactDiscountedPrice,
      currencyCode: variant?.price?.currencyCode,
      item_name: payload.title,
    };
  } catch (e) {
    return {};
  }
};

// Sync X-Sell variants to match the current PDP variant selection
const syncInitialPdpVariant = () => {
  if (!deal.value || !props.product) return;
  const sku = route.query.sku;
  if (!sku) return;
  const variant = props.product.variants?.edges?.find((e) => e.node.sku?.toLowerCase() === sku.toLowerCase());
  if (variant) syncVariantById(variant.node.id);
};

// Helper function to handle query params after deal initialization
const handleQueryParamsAfterDealInit = () => {
  syncInitialPdpVariant();
  const params = getAddToCartParamsFromUrl();
  if (params && params.target === 'xsell' && bundleAvailable.value) {
    addToCart(params.quantity);
  }
};

// Handler for PDP variant changes - syncs variant selection to x-sell products
const onPdpVariantChanged = ({ variantId }) => {
  if (deal.value && variantId) {
    syncVariantById(variantId);
  }
};

// Lifecycle hooks
onMounted(() => {
  // Quit early if we don't want to show the xsell teasers
  if (appStore.showXSellTeasers === false) {
    isLoading.value = false;
    return;
  }

  Bus.on('pdp-variant-changed', onPdpVariantChanged);

  if ($pickystory.isPickyStoryReady()) {
    initDeal().then(handleQueryParamsAfterDealInit);
  } else {
    Bus.on('pickystory-ready', () => {
      initDeal().then(handleQueryParamsAfterDealInit);
    });
  }
});

onBeforeUnmount(() => {
  Bus.off('pdp-variant-changed', onPdpVariantChanged);
});
</script>
